import React, { useContext, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { AuthContext } from '../reducers/Auth/AuthContext';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import Button from '@mui/material/Button';
import { updateProduct } from '../apis/updateProduct';
import { addNewProduct } from '../apis/addNewProduct';
import { deleteProduct } from '../apis/deleteProduct';
import { removeImageFromBucket } from '../apis/removeImageFromBucket';
import { updateProductImage } from '../apis/updateProductImage';
import { EditField } from './EditField';
import { EditCategory } from './EditCategory';
import { EditToppingGroup } from './EditToppingGroup';
import { EditYesNo } from './EditYesNo';
import { EditContentsTags } from './EditContentsTags';
import { EditNutritionTags } from './EditNutritionTags';
import { EditAlergensTags } from './EditAlergensTags';
import { EditDetails } from './EditDetails';
import { EditImage } from './EditImage';
import { EditChecked } from './EditChecked';
import AreYouSureYesNo from './AreYouSureYesNo';
import { EditExtraCategories } from './EditExtraCategories';
import BarLoader from 'react-spinners/BarLoader';
import { uuid } from '../utils/uuid';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { COLORS } from '../utils/colors';
import config from '../utils/config';
import PublishButton from './PublishButton';

const ManageProduct = (props) => {
  const { productId, categoryId, close } = props;
  const [authState] = useContext(AuthContext);
  const initalFile = null;
  const [file, setFile] = useState(initalFile);
  const [placeState] = useContext(PlaceStoreContext);
  const [initialData, setInitialData] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [productDelete, setProductDelete] = useState({
    visible: false,
    isDeleting: false,
  });

  const {
    products: { productId__product },
    categories: { categoryId__category },
    services: { serviceId__service },
  } = placeState;

  const product = productId__product[productId];
  const category = categoryId__category[categoryId || product.category_id];
  const service = serviceId__service[category.service_id];

  const {
    user: { placeId, userId, role },
  } = authState;

  const isUnitpay = role === 'unitpay';
  const isBasic = role === 'basic';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!product) return;

    const newProduct = product.id === 'new';

    const imageUrl = `${config.uri.bucket}/${placeId}/high/${
      product.id
    }.jpg?time=${new Date().getTime()}`;
    const imageDetails = {
      image: product.has_image ? imageUrl : null,
      local: false,
    };

    const initialValues = {
      active: product.active,
      image: imageDetails,
      showable: product.promote === true,
      name: product.name,
      code: newProduct ? '' : product.code || '',
      cost: product.cost.toString() || '0',
      currency: product.currency || 'lei',
      serviceId: service.id || '',
      weight: product.weight || '',
      consumers: product.consumers || '',
      details: product.details || '',
      description: product.description || '',
      category: category.id || product.category_id,
      extraCategories: product.extra_cat || [],
      toppingGroup: product.toppings_group_id || '',
      delivery: Boolean(product?.delivery),
      contents: product.contents || [],
      nutrition: product.nutrition || {
        totalQty: 0,
        portion: {},
        '100g': {},
        additives: null,
        others: null,
      },
      alergens: product.alergens || [],
      tags: product.tags || [],
      externalLink: product.external_link || '',
    };

    setInitialData({
      initialValues,
      imageUrl: imageDetails.image,
    });
  }, [productId, placeState]);

  const handleProductDelete = () => {
    setProductDelete({ visible: true, isDeleting: false });
  };

  const productOnNo = () => {
    setProductDelete({ visible: false, isDeleting: false });
  };

  const productOnYes = async () => {
    setProductDelete({ visible: true, isDeleting: true });
    await deleteProduct(placeId, productId);
    close();
  };

  if (!productId__product[productId] || initialData === null) {
    return null;
  }

  if (productDelete.isDeleting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `calc(90vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
          height: '80vh',
          margin: '0 auto',
        }}
      >
        <Grid container spacing={2} style={{ padding: '16px' }}>
          <Grid item xs={12}>
            <AreYouSureYesNo
              onNo={productOnNo}
              onYes={productOnYes}
              loading={productDelete.isDeleting}
              loadingMessage="Deleting product ..."
              warning="THIS PRODUCT WILL BE DELETED!"
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container style={{ marginTop: '40px' }}>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            showable: Yup.boolean().required('Required'),
            name: Yup.string()
              .min(3, 'Must have at least 3 chars')
              .max(128, 'Must be 128 characters or less')
              .required('Required'),
            code: Yup.string().max(36, 'Must be 36 characters or less'),
            cost: Yup.string()
              .matches(/^(\d+(\.\d{0,2})?|\.?\d{1,2})$/, 'Not a valid cost.')
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              )
              .test('value', 'Must be between 0 and 10000000', (value) => {
                return Number(value) >= 0 && Number(value) < 10000000;
              })
              .required('Required'),
            currency: Yup.string()
              .max(16)
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              )
              .required('Required'),
            weight: Yup.string().max(64, 'Must be 64 characters or less'),
            consumers: Yup.string().max(32),
            details: Yup.string().max(2048),
            category: Yup.string()
              .test('text', 'Must be a valid category', (value) => {
                return categoryId__category[value] !== undefined;
              })
              .test(
                'value',
                'Must have at least one character',
                (value) => value && value.trim().length >= 1,
              )
              .required('Required'),
            delivery: Yup.boolean().required('Required'),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            if (!placeId) return;
            const newProduct = product.id === 'new';
            setSubmitting(true);

            let hasImage = false;

            const selectedProductId = newProduct ? uuid() : product.id;
            if (file) {
              hasImage = true;
              console.log('Updating Image');
              await updateProductImage(placeId, selectedProductId, file);
              console.log('Done updating image');
            }

            if (product.has_image && removeImage && !newProduct) {
              console.log('Removing image from product');
              await removeImageFromBucket(placeId, 'product', product.id);
              console.log('Done removing image from product');
            }

            values.toppingGroup = values.toppingGroup.length === 0 ? null : values.toppingGroup;
            values.userId = userId;

            if (newProduct) {
              console.log('Adding New Product');
              values.hasImage = hasImage;
              await addNewProduct(placeId, selectedProductId, values);
              console.log('Done adding new product');
            } else {
              console.log('Updating Product');
              await updateProduct(placeId, productId, values);
              console.log('Done updating product');
            }

            if (file) {
              setFile(null);
            }
            setSubmitting(false);
            close();
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `calc(100vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
                    height: '80vh',
                    margin: '0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES AND PUBLISHING ...
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );

            const submitDisabled =
              isSubmitting ||
              (file === null && _.isEqual(initialData.initialValues, values) && !removeImage);

            const title =
              product.id === 'new' && categoryId
                ? `NEW PRODUCT FOR ${categoryId__category[categoryId].name.toUpperCase()}`
                : product.name.toUpperCase();

            return (
              <Form encType="multipart/form-data">
                <Grid container spacing={2} justify="center" style={{ maxWidth: '500px' }}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        padding: 0,
                        fontWeight: 'bold',
                        color: COLORS.INFO,
                      }}
                    >
                      {title}
                    </p>
                    <PublishButton
                      name="PUBLISH"
                      disabled={!removeImage === false ? false : submitDisabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditYesNo label="Active" name="active" />
                  </Grid>
                  {!isBasic && <Grid item xs={12}>
                    <EditImage
                      initialImage={initialData.imageUrl}
                      image={values.image}
                      removeImage={removeImage}
                      setRemoveImage={setRemoveImage}
                      product={product}
                      image={values.image}
                      setFieldValue={setFieldValue}
                      setFile={setFile}
                      file={file}
                      height="165px"
                    />
                  </Grid>}
                  <Grid item xs={12}>
                    <EditChecked
                      type="checkbox"
                      label="Product image shown on category"
                      name="showable"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingBottom: '15px' }}>
                    <EditField label="Name (128 max characters)" name="name" />
                  </Grid>
                  <Grid item xs={12}>
                    <EditField
                      label="Code (36 max characters)"
                      name="code"
                      style={{ paddingBottom: '15px' }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <EditField label="Cost" name="cost" style={{ paddingBottom: '15px' }} />
                  </Grid>
                  <Grid item xs={3}>
                    <EditField
                      label={`Currency`}
                      name="currency"
                      style={{ paddingBottom: '15px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditField label={`Units`} name="weight" style={{ paddingBottom: '15px' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <EditField
                      label="Recommended Consumers"
                      name="consumers"
                      style={{ paddingBottom: '15px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditDetails
                      label="Details (2048 max characters)"
                      name="details"
                      style={{ paddingBottom: '15px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditDetails
                      label="Description (2048 max characters)"
                      name="description"
                      style={{ paddingBottom: '15px' }}
                    />
                  </Grid>
                  {product.id !== 'new' && (
                    <Grid item xs={12}>
                      <EditCategory
                        label="Main Category"
                        name="category"
                        multiple={false}
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <EditExtraCategories
                      mainCategory={values.category}
                      extraCategories={values.extraCategories}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditToppingGroup
                      label="Topping Group"
                      name="toppingGroup"
                      selected={initialData.initialValues.toppingGroup}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditYesNo label="Delivery" name="delivery" style={{ paddingBottom: '15px' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <EditContentsTags
                      product={product}
                      type="contents"
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditNutritionTags
                      product={product}
                      type="nutrition"
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditAlergensTags
                      product={product}
                      type="alergens"
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EditContentsTags product={product} type="tags" setFieldValue={setFieldValue} />
                  </Grid>
                  {isUnitpay && <Grid item xs={12}>
                    <EditField label="External Link" name="externalLink" />
                  </Grid>}
                  {productId !== 'new' && (
                    <Grid item xs={12} style={{ marginTop: 16 }}>
                      {!productDelete.visible ? (
                        <Button variant="contained" color="error" onClick={handleProductDelete}>
                          <span>DELETE</span>
                          <DeleteForeverIcon className="s-24-i txt-white ml-16" />
                        </Button>
                      ) : (
                        <div className="ml-16">
                          <AreYouSureYesNo
                            onNo={productOnNo}
                            onYes={productOnYes}
                            loading={productDelete.isDeleting}
                            loadingMessage="Deleting product ..."
                            warning="THIS PRODUCT WILL BE DELETED!"
                          />
                        </div>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!removeImage === false ? false : submitDisabled}
                  >
                    PUBLISH
                  </Button>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ManageProduct;
